/** Canonical Pulse envelope — every key always present; unknown → null. */

export const PULSE_SCHEMA_VERSION = '1.0';

export type PulseEventName =
  | 'click'
  | 'view'
  | 'login'
  | 'signup'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'view_cart'
  | 'start_checkout'
  | 'purchase'
  | 'consent'
  | 'context'
  | 'session_stats';

/** Braze `getMappedItems` line item — combo rows may include nested `items`. */
export type PulseItem = {
  item_name?: string | null;
  price?: number | null;
  size?: string | null;
  image?: string | null;
  quantity?: number | null;
  mods_added?: string[] | null;
  items?: PulseItem[] | null;
};

export type ClickPayload = {
  category: string | null;
  action: string | null;
  label: string | null;
  value: string | number | null;
};

export type PulseSource = 'web' | 'mobile_web';

export type PulseEvent = {
  schema_version: string;
  event_name: PulseEventName | string;
  event_id: string;
  event_time: number;
  source: PulseSource;
  user_id: string | number | null;
  email: string | null;
  phone_number: string | null;
  new_user_indicator: boolean | null;
  click_id: string | null;
  page_id: string | null;
  view_id: string | null;
  cart_id: string | number | null;
  checkout_id: string | null;
  page_url: string | null;
  page_path: string | null;
  http_referrer: string | null;
  conversion_id: string | number | null;
  sub_total_amount: number | null;
  transaction_amount: number | null;
  delivery_amount: number | null;
  service_amount: number | null;
  tax_amount: number | null;
  tip: number | null;
  items_payload: PulseItem[] | null;
  currency: string;
  cookie_consent_advertisement: boolean | null;
  cookie_consent_functional: boolean | null;
  cookie_consent_necessary: boolean | null;
  snapchat_tracking_id: string | null;
  tiktok_tracking_id: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  cro_variants: string | null;
  /** GA4 gtag client id — filled at Pulse send time for server-side GA4 stitching. */
  client_id: string | null;
  /** GA4 gtag session id — filled at Pulse send time for server-side GA4 stitching. */
  session_id: string | null;
  /** Click taxonomy (ARD). Null on non-click events until Ticket 3. */
  category: string | null;
  action: string | null;
  label: string | null;
  click_payload: ClickPayload | null;
  additional_params: Record<string, unknown> | null;
};

export type PulseEventPartial = Partial<Omit<PulseEvent, 'schema_version' | 'source'>> & {
  event_name: PulseEventName | string;
  [key: string]: unknown;
};

export const createEmptyPulseEvent = (
  event_name: PulseEventName | string
): PulseEvent => ({
  schema_version: PULSE_SCHEMA_VERSION,
  event_name,
  event_id: '', // filled by tracker
  event_time: 0, // filled by tracker
  source: 'web',
  user_id: null,
  email: null,
  phone_number: null,
  new_user_indicator: null,
  click_id: null,
  page_id: null,
  view_id: null,
  cart_id: null,
  checkout_id: null,
  page_url: null,
  page_path: null,
  http_referrer: null,
  conversion_id: null,
  sub_total_amount: null,
  transaction_amount: null,
  delivery_amount: null,
  service_amount: null,
  tax_amount: null,
  tip: null,
  items_payload: null,
  currency: 'US',
  cookie_consent_advertisement: null,
  cookie_consent_functional: null,
  cookie_consent_necessary: null,
  snapchat_tracking_id: null,
  tiktok_tracking_id: null,
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  cro_variants: null,
  client_id: null,
  session_id: null,
  category: null,
  action: null,
  label: null,
  click_payload: null,
  additional_params: null,
});
